import { Card } from '@components/atoms/Card';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import Alert from '@components/atoms/Alert';
import { formatActivityTime } from '@utils/formatDate';
import { ActivityHeader } from '../ActivityHeader';
import { TaskName } from '../TaskName';
import { isImageFile } from '@utils/documentUtils';
import { SmallDocumentList } from '@components/molecules/SmallDocumentList';
import { useTranslation } from '@hooks/useTranslation';

export const ActivityNote = ({
  activity,
  handleOpenTaskDetail,
}: {
  activity: any;
  handleOpenTaskDetail?: (taskId: string) => void;
}) => {
  const tActivityCards = useTranslation('activityCards');
  const documents = activity.note?.documents || [];
  const firstImageDocument = documents.find((doc: any) =>
    isImageFile(doc.name),
  );
  const remainingDocuments = documents.filter(
    (doc: any, index: number) =>
      doc !== firstImageDocument || (doc === firstImageDocument && index > 0),
  );

  // Check if attemptAt exists and is different from createdAt (indicates offline creation)
  const hasAttemptedAt =
    activity.attemptAt &&
    activity.createdAt &&
    new Date(activity.attemptAt).getTime() !== new Date(activity.createdAt).getTime();

  const rightLabel = (
    <div className="flex flex-col items-end gap-0.5">
      <p className="text-sm text-neutral-500">
        {tActivityCards.note.addedNote.replace('{time}', formatActivityTime(activity.createdAt))}
      </p>
      {hasAttemptedAt && (
        <p className="text-[12px] text-neutral-400">
          {tActivityCards.note.attemptedAt.replace('{time}', formatActivityTime(activity.attemptAt))}
        </p>
      )}
    </div>
  );

  return (
    <Card key={activity.activityId} className="w-full">
      <ActivityHeader activity={activity} rightLabel={rightLabel} />
      <div className="flex gap-2 w-full">
        <div className="flex flex-col flex-1 gap-1">
          <div className="flex gap-6 mt-3">
            <div className="flex gap-1 items-baseline">
              <p className="text-sm text-neutral-800">{tActivityCards.projectLabel}</p>
              <p className="text-sm text-neutral-800 font-semibold capitalize">
                {activity.project?.name || tActivityCards.notAvailable}
              </p>
            </div>
            {activity.task?.name && (
              <TaskName
                size="sm"
                task={activity.task}
                onOpenTask={handleOpenTaskDetail}
                taskId={activity.taskId || activity.task?.taskId}
              />
            )}
          </div>
          {activity.note?.name && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-neutral-800 font-semibold">
                {activity.note.name}
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <div className="flex-1">
              {activity.note?.description && (
                <Alert className="mt-2 h-full">
                  <p>{activity.note?.description}</p>
                </Alert>
              )}
            </div>
            <div>
              {firstImageDocument && (
                <div
                  onClick={() => {
                    window.open(firstImageDocument.url, '_blank');
                  }}
                  className="relative cursor-pointer">
                  <div className="flex p-1 justify-center items-center absolute bottom-1 left-1 bg-neutral-100/90 rounded-md">
                    <ZoomInOutlinedIcon fontSize="small" />
                  </div>
                  <img
                    className="w-36 h-36 object-cover rounded-md mt-2"
                    src={firstImageDocument.url}
                    alt={firstImageDocument.name || 'note'}
                  />
                </div>
              )}
            </div>
          </div>
          {remainingDocuments.length > 0 && (
            <div className="mt-3">
              <p className="text-sm text-neutral-700 font-medium mb-2">
                {tActivityCards.note.attachments.replace('{n}', String(remainingDocuments.length))}
              </p>
              <SmallDocumentList documents={remainingDocuments} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
